/**
 * IncidentFilters Component - Phase 13
 * 
 * Filter bar for incident audit trail.
 */

import { useState } from 'react';
import {
  Filter,
  Search,
  X,
  Calendar,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getDomainDisplayLabel, getSourceTypeDisplayLabel, SORT_OPTIONS } from '@/lib/offlineAudit';

/**
 * IncidentFilters Component
 */
export function IncidentFilters({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  incidentCount
}) {
  const [showDatePicker, setShowDatePicker] = useState(null); // 'from' | 'to'

  // Count active filters
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'sortBy') return false;
    if (key === 'searchText') return !!value;
    if (key === 'unresolvedOnly') return value === true;
    return value !== null;
  }).length;

  // Handle filter change
  const handleChange = (key, value) => {
    onFilterChange({ [key]: value === 'all' ? null : value });
  };

  // Format date for display
  const formatDate = (ts) => {
    if (!ts) return null;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <div className="space-y-3" data-testid="incident-filters">
      {/* Search and Quick Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search incidents..."
            value={filters.searchText || ''}
            onChange={(e) => handleChange('searchText', e.target.value)}
            className="pl-9"
            data-testid="incident-search-input"
          />
        </div>

        {/* Quick filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Checkbox
              id="unresolvedOnly"
              checked={filters.unresolvedOnly || false}
              onCheckedChange={(checked) => handleChange('unresolvedOnly', checked)}
            />
            <Label htmlFor="unresolvedOnly" className="text-sm cursor-pointer">
              Unresolved only
            </Label>
          </div>
        </div>
      </div>

      {/* Filter Dropdowns Row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span className="text-sm">Filters:</span>
        </div>

        {/* Status */}
        <Select
          value={filters.status || 'all'}
          onValueChange={(v) => handleChange('status', v)}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="retrying">Retrying</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="conflict">Conflict</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="manual_review">Manual Review</SelectItem>
          </SelectContent>
        </Select>

        {/* Severity */}
        <Select
          value={filters.severity || 'all'}
          onValueChange={(v) => handleChange('severity', v)}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>

        {/* Domain */}
        <Select
          value={filters.domain || 'all'}
          onValueChange={(v) => handleChange('domain', v)}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="Domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Domains</SelectItem>
            {filterOptions?.domains?.map(domain => (
              <SelectItem key={domain} value={domain}>
                {getDomainDisplayLabel(domain)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Source Type */}
        <Select
          value={filters.sourceType || 'all'}
          onValueChange={(v) => handleChange('sourceType', v)}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {filterOptions?.sourceTypes?.map(src => (
              <SelectItem key={src} value={src}>
                {getSourceTypeDisplayLabel(src)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Location */}
        {filterOptions?.locations?.length > 0 && (
          <Select
            value={filters.locationId || 'all'}
            onValueChange={(v) => handleChange('locationId', v)}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {filterOptions.locations.map(loc => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Terminal */}
        {filterOptions?.terminals?.length > 0 && (
          <Select
            value={filters.terminalId || 'all'}
            onValueChange={(v) => handleChange('terminalId', v)}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue placeholder="Terminal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Terminals</SelectItem>
              {filterOptions.terminals.map(term => (
                <SelectItem key={term} value={term}>{term}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Date Range */}
        <Popover open={showDatePicker === 'from'} onOpenChange={(open) => setShowDatePicker(open ? 'from' : null)}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <Calendar className="h-4 w-4 mr-1" />
              {filters.dateFrom ? formatDate(filters.dateFrom) : 'From'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <CalendarComponent
              mode="single"
              selected={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
              onSelect={(date) => {
                handleChange('dateFrom', date ? date.getTime() : null);
                setShowDatePicker(null);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover open={showDatePicker === 'to'} onOpenChange={(open) => setShowDatePicker(open ? 'to' : null)}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <Calendar className="h-4 w-4 mr-1" />
              {filters.dateTo ? formatDate(filters.dateTo) : 'To'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <CalendarComponent
              mode="single"
              selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
              onSelect={(date) => {
                handleChange('dateTo', date ? date.setHours(23, 59, 59, 999) : null);
                setShowDatePicker(null);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Sort */}
        <Select
          value={filters.sortBy || SORT_OPTIONS.NEWEST_FIRST}
          onValueChange={(v) => handleChange('sortBy', v)}
        >
          <SelectTrigger className="w-36 h-8">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SORT_OPTIONS.NEWEST_FIRST}>Newest First</SelectItem>
            <SelectItem value={SORT_OPTIONS.OLDEST_FIRST}>Oldest First</SelectItem>
            <SelectItem value={SORT_OPTIONS.SEVERITY_DESC}>By Severity</SelectItem>
            <SelectItem value={SORT_OPTIONS.UNRESOLVED_FIRST}>Unresolved First</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" className="h-8" onClick={onClearFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {incidentCount} incident{incidentCount !== 1 ? 's' : ''}
          {activeFilterCount > 0 && ' (filtered)'}
        </span>
      </div>
    </div>
  );
}

export default IncidentFilters;
